import { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  SubsidiaryInterface,
  ConfigurationInterface,
  PromotionInterface,
  PaymentPlanInterface
} from '@e3labs/isa-data-dictionary';
import {
  LastOrderDetail,
  PixPaymentProps,
  ProductListBonificationRestrictionResponse,
  SendOrderProps,
  OrderItemCreateInterface,
  RequiredBasketResponse
} from './order-interface';
import { ORDER_STATUS, ORDER_TYPE_SEND } from './order-constants';
import api from '~/services/api';
import { toDecimals } from '~/utils/format';

import { OrderInterface } from '@e3labs/isa-data-dictionary';
import { CancelOrderTotvsPayloadTypes } from './order-actions';

import { groupIntoChunks } from './order-utils';
import productService from '../product/product-service';
import { validateBatch } from '~/store/modules/order/order-utils';
import notification from '~/services/notification';
import { formatMessage } from '~/i18n/translate';
import { getTax } from '../tributacao/tributacao-service';

const endpoints = {
  [ORDER_TYPE_SEND.ORDER]: '/order/v2',
  [ORDER_TYPE_SEND.BUDGET]: '/order/v2/budget',
  [ORDER_TYPE_SEND.LOAD]: '/order/v2/load',
  [ORDER_TYPE_SEND.WAITING_APPROVAL]: '/order/v2/send-to-approval',
  [ORDER_TYPE_SEND.PAYMENT_LINK]: '/payment'
};

class OrderService {
  getTotalOrders(params?: any, config?: AxiosRequestConfig) {
    return api.get('/order/orders-status-cost', {
      params: {
        orderNumber: params?.orderNumber ?? undefined,
        externalOrder: params?.orderExternalNumber ? true : params?.externalOrder,
        orderExternalNumber: params?.orderExternalNumber ?? undefined,
        invoiceNumber: params?.invoiceNumber ?? undefined,
        clientExternalCode: params?.clientExternalCode ?? undefined,
        orderStatus: params?.status!?.length > 0 ? params?.status?.join(',') : null,
        orderType: params?.orderType!?.length > 0 ? params?.orderType?.join(',') : null,
        usersExternalCodes: params?.ids!?.length > 0 ? params?.ids?.join(',') : null,
        ...(params?.startDate && {
          startDate: params?.startDate,
          endDate: params?.endDate
        }),
        getNames: params?.getNames,
        subsidiaryExternalCodes: params?.subsidiaryExternalCodes ?? undefined
      }
    });
  }

  getClients(
    { skip = 0, limit = 20, userExternalCode = '', queryString = '', engine = 'externalCode,name' },
    opts?: any
  ): Promise<AxiosResponse<any[] | null>> {
    return api.get<any[]>('/client', {
      params: {
        skip,
        limit,
        fieldsToShow: 'externalCode,name',
        userExternalCode,
        queryString,
        engine
      },
      ...opts
    });
  }

  getCountOrders(params?: any, config?: AxiosRequestConfig) {
    return api.get('/order/v2/count', {
      ...config,
      params: {
        orderNumber: params?.orderNumber ?? undefined,
        externalOrder: params?.orderExternalNumber ? true : params?.externalOrder,
        orderExternalNumber: params?.orderExternalNumber ?? undefined,
        invoiceNumber: params?.invoiceNumber ?? undefined,
        clientExternalCode: params?.clientExternalCode ?? undefined,
        orderStatus: params?.orderStatus!?.length > 0 ? params?.orderStatus?.join(',') : undefined,
        orderType: params?.orderType!?.length > 0 ? params?.orderType?.join(',') : undefined,
        usersExternalCodes: params?.ids!?.length > 0 ? params?.ids?.join(',') : undefined,
        ...(params?.startDate && {
          startDate: params?.startDate,
          endDate: params?.endDate
        }),
        getNames: params?.getNames,
        subsidiaryExternalCodes: params?.subsidiaryExternalCodes ?? undefined
      }
    });
  }

  orderPdf(id: string, sendPdfByEmail: boolean = false, clientInfo: any) {
    return api.post(
      `/order/pdf/${id}?sendPDF=${sendPdfByEmail}`,
      {
        clientInfo
      },
      {
        responseType: 'blob'
      }
    );
  }

  downloadExcel(id: string) {
    return api.get(`/order/${id}/cotefacil-report`, {
      responseType: 'blob'
    });
  }

  getOrders(params?: any, config?: AxiosRequestConfig) {
    return api.get('/order/v2', {
      ...config,
      params: {
        orderNumber: params?.orderNumber ?? undefined,
        externalOrder: params?.externalOrder,
        orderExternalNumber: params?.orderExternalNumber ?? undefined,
        invoiceNumber: params?.invoiceNumber ?? undefined,
        skip: params?.skip ?? 0,
        limit: params?.limit ?? 10,
        clientExternalCode: params?.clientExternalCode ?? undefined,
        orderStatus: params?.status!?.length > 0 ? params?.status?.join(',') : null,
        orderType: params?.orderType!?.length > 0 ? params?.orderType?.join(',') : null,
        usersExternalCodes: params?.ids!?.length > 0 ? params?.ids?.join(',') : null,
        ...(params?.startDate && {
          startDate: params?.startDate,
          endDate: params?.endDate
        }),
        orderAsc: params?.orderAsc,
        getNames: params?.getNames,
        subsidiaryExternalCodes: params?.subsidiaryExternalCodes ?? undefined
      }
    });
  }

  getOrder(id: string) {
    return api.get(`/order/v2/${id}`);
  }

  getVinculatedOrder(internalCode: string) {
    return api.get(`/order/v2/vinculated-orders`, {
      params: {
        internalCode
      }
    });
  }

  getOrderProducts({ filters }, skip: number = 0, limit: number = 100): Promise<AxiosResponse<any[] | null>> {
    return api.get<any[]>('/product', {
      params: {
        ...filters,
        skip,
        limit,
        fieldsToShow: 'externalCode,name,pictures,brand,stocks,prices,multiplesSubsidiary',
        isRecovery: true
      }
    });
  }

  getDeliveries({
    externalCodes = undefined,
    limit = 0,
    skip = 0
  }: {
    externalCodes?: string;
    limit?: number;
    skip?: number;
  }) {
    return api.get(`/domain/providers`, {
      params: {
        externalCodes,
        limit,
        skip
      }
    });
  }

  deleteOrder(id: string) {
    return api.delete(`/order/v2/${id}`);
  }

  cancelBudget(order: OrderInterface) {
    return api.put(`/order/v2/budget?realmID=${order?.vendor?.externalCode}_${new Date().getTime().toString()}`, {
      ...order,
      orderStatus: ORDER_STATUS.CANCELED_BUDGET
    });
  }

  getSellers(): Promise<AxiosResponse<any[] | null>> {
    return api.get<any[]>('/user', {
      params: {}
    });
  }

  resendOrder(id: string): Promise<AxiosResponse<any[] | null>> {
    return api.post<any[]>(`/order/v2/resend/${id}`);
  }

  sendOrderPayment(type: string, { order, sendOrderInPDF, realmID, cellphoneNumber, email, numberOfInstallments }) {
    return api.post<any[]>(`${endpoints[type]}`, {
      realmID,
      order,
      sendPdf: sendOrderInPDF,
      cellphoneNumber,
      email,
      numberOfInstallments
    });
  }

  sendOrder(type: string, isEdit: boolean = false, { order, sendOrderInPDF, realmID, orderId }: SendOrderProps) {
    return isEdit && order._id
      ? type === ORDER_TYPE_SEND.BUDGET
        ? //! editar o orçamento
          api.put(
            endpoints[ORDER_TYPE_SEND.BUDGET],
            {
              ...order,
              orderId,
              realmID
            },
            {
              params: {
                realmID
              }
            }
          )
        : type === ORDER_TYPE_SEND.LOAD
        ? //! editar o waitingLoad(aguardando carga)
          api.put(endpoints[ORDER_TYPE_SEND.LOAD], {
            ...order,
            _id: orderId,
            realmID
          })
        : //! editar o order (waitingApproval)
          api.put(`${endpoints[ORDER_TYPE_SEND.ORDER]}/${orderId}`, {
            orderUpdate: { ...order, _id: orderId }
          })
      : api.post(`${endpoints[type]}`, order, {
          params: {
            realmID,
            sendPdf: sendOrderInPDF
          }
        });
  }

  getRequiredBasket(
    clientExternalCode?: string,
    subsidiaryExternalCode?: string
  ): Promise<AxiosResponse<RequiredBasketResponse>> {
    return api.get('/order/basket-order-exists', {
      params: {
        clientExternalCode,
        subsidiaryExternalCode
      }
    });
  }

  getOrderSplitValidation({ order, bonificationApproval }: { order: any; bonificationApproval: boolean }) {
    return api.post(`/order/v2/split-order-report`, {
      order,
      bonificationApproval
    });
  }

  getDetailedProductBonificated(
    { filters },
    skip: number = 0,
    limit: number = 50
  ): Promise<AxiosResponse<any[] | null>> {
    return api.get<any[]>('/product/detailed', {
      params: {
        ...filters,
        skip,
        limit
      }
    });
  }

  generatePix(orderId: string): Promise<AxiosResponse<PixPaymentProps>> {
    return api.post('/payment/pix/', {
      orderId
    });
  }

  getRestrictionBonificationProductCheck({
    params
  }): Promise<AxiosResponse<ProductListBonificationRestrictionResponse[] | null>> {
    return api.post('/sales-restriction-group/check', {
      ...params
    });
  }

  getLastOrderDetail(clientExternalCode: string): Promise<AxiosResponse<LastOrderDetail>> {
    return api.get<LastOrderDetail>('/order/v2/last-order-details', {
      params: {
        clientExternalCode
      }
    });
  }

  cancelOrderTotvs({ id, reasonCancellation }: CancelOrderTotvsPayloadTypes) {
    return api.post(`/order/v2/cancel/${id}`, {
      id,
      reasonCancellation
    });
  }

  getSoldPrice(discountPercentage: number, orderItem: any, decimals: number) {
    const discount = discountPercentage >= 100 ? 100 : discountPercentage <= 0 ? 0 : discountPercentage;
    let baseValue = orderItem?.baseValue;

    let soldPrice =
      toDecimals(baseValue * ((100 - toDecimals(discount, decimals ?? 2)) / 100), decimals ?? 2) *
      (orderItem?.packing?.packingQuantity ?? 1) *
      (orderItem?.packing?.priceFactor ?? 1);

    return {
      ...orderItem,
      discountPercentage: discount,
      discount: discount > 0,
      soldPrice,
      soldPriceWithPacking: soldPrice,
      totalPrice: toDecimals(orderItem.soldQuantity * soldPrice, decimals ?? 2),
      totalPriceWithPacking: toDecimals(orderItem.soldQuantity * soldPrice, decimals ?? 2)
    };
  }

  async refreshOrderItems(
    order: any,
    subsidiary: SubsidiaryInterface,
    configuration: ConfigurationInterface,
    priceTableCode: string,
    paymentPlan: PaymentPlanInterface,
    tenantID?: string
  ) {
    const decimals = configuration?.decimals ?? 2;
    const allowPriceUpdate = configuration?.allowPriceUpdate;
    const hasTaxation = configuration?.taxation;

    const itemChunks = groupIntoChunks(order.orderItems, 5);
    let refreshedItems: OrderItemCreateInterface[] = [];

    const minBatchExpiryDays = order?.client?.minimumBatchExpiryDate ?? configuration?.defaultBatchExpiryDate;

    for (const chunk of itemChunks) {
      const promises = chunk.map(async (item: any) => {
        try {
          const { data: freshProduct } = await productService.getById({
            _id: item._id,
            subsidiaryExternalCode: subsidiary?.externalCode ?? order.orderDetails?.subsidiaryExternalCode,
            subsidiaryStockExternalCode:
              item.subsidiaryStockExternalCode ?? order.orderDetails?.subsidiaryStock?.externalCode ?? null,
            usePaymentPlansGroup: order.client?.usePaymentPlansGroup,
            paymentPlanExternalCodes: order.client?.usePaymentPlansGroup
              ? [
                  order.orderDetails?.paymentPlanGroup1ExternalCode,
                  order.orderDetails?.paymentPlanGroup2ExternalCode
                ].join(',')
              : order.orderDetails?.paymentPlanExternalCode,
            applyTax: order.client?.applyTax,
            applyTax2: hasTaxation ? order.client?.applyTax2 : undefined,
            applyTaxST: hasTaxation ? order.client?.applyTaxST : undefined,
            comparesMinimumPriceWithST: configuration?.comparesMinimumPriceWithST,
            useTagFocus: configuration?.useTagFocus,
            decimals,
            priceTableCode,
            state: order.client?.commercialInfo?.state,
            clientDiscountPercentage: order.client?.discountPercentage ?? 0,
            governmentalCode: order?.client?.governmentalCode,
            finalConsumer: order?.client?.finalConsumer ?? null
          });

          const { isValidBatch, totalAvailableInValidBatches } = await validateBatch({
            subsidiaryExternalCode: subsidiary?.externalCode ?? order.orderDetails?.subsidiaryExternalCode,
            orderItem: { ...item, product: freshProduct },
            minBatchExpiryDays,
            notify: false
          });

          let finalQuantity = item.soldQuantity;

          if (!isValidBatch) {
            if (totalAvailableInValidBatches <= 0) {
              notification.warning(
                formatMessage('orderDetailBlockBatchRemove', {
                  productName: freshProduct.name,
                  minBatchExpiryDays
                })
              );
              return null;
            } else {
              finalQuantity = totalAvailableInValidBatches;
              notification.warning(
                formatMessage('orderDetailBlockBatchQuantity', {
                  productName: freshProduct.name,
                  totalAvailableInValidBatches
                })
              );
            }
          }

          const baseValue = toDecimals(freshProduct.baseValue, decimals);
          const discount = toDecimals(item.discountPercentage, decimals) || 0;

          let soldPrice = allowPriceUpdate ? baseValue : item.baseValue;

          if (discount !== 0) {
            soldPrice = toDecimals(soldPrice - (soldPrice * discount) / 100, decimals);
          }

          const oldPacking = item.package;
          let currentPacking = freshProduct?.packing?.[0] ?? null;

          if (oldPacking) {
            currentPacking = freshProduct.packing?.find(p => p.barCode === oldPacking.barCode) ?? currentPacking;
          }

          let packingInfos = item.packingInfos;

          if (currentPacking) {
            const priceFactor = currentPacking.priceFactor ?? 1;
            const packingQuantity = currentPacking.packingQuantity ?? 1;

            if (
              oldPacking?.barCode === currentPacking.barCode &&
              (currentPacking.packingQuantity !== oldPacking.packingQuantity ||
                currentPacking.priceFactor !== oldPacking.priceFactor)
            ) {
              soldPrice = toDecimals(soldPrice * packingQuantity * priceFactor, decimals);
            } else {
              soldPrice = allowPriceUpdate
                ? toDecimals(soldPrice * packingQuantity * priceFactor, decimals)
                : item.soldPrice;
            }

            packingInfos = {
              ...currentPacking,
              unit: currentPacking.packingUnit,
              type: currentPacking.packingType,
              availableQuantity: toDecimals((freshProduct.availableQuantity ?? 0) / packingQuantity, 2)
            };
          }

          let tributacao = null;
          if (configuration?.taxation) {
            tributacao = getTax({
              client: order.client,
              configuration,
              paymentPlan,
              product: freshProduct,
              shippingType: order?.orderDetails?.shippingType,
              soldPrice,
              soldQuantity: finalQuantity,
              tenantID,
              // @ts-ignore
              tributacao: freshProduct.tributacao?.data,
              vendorDiscountPercentage: item.discountPercentage
            });
          }

          return {
            ...item,
            ...freshProduct,
            soldQuantity: finalQuantity,
            packing: freshProduct.packing || [],
            baseValue,
            package: currentPacking,
            packingInfos,
            soldPrice,
            soldPriceWithPacking: soldPrice,
            availableQuantity: freshProduct.availableQuantity,
            barCode: currentPacking?.barCode || freshProduct.barCode,
            tabulatedPrice: toDecimals(freshProduct.tabulatedPrice, decimals),
            price: freshProduct.price,
            prices: freshProduct.prices,
            tributacao
          };
        } catch {
          return null;
        }
      });

      const chunkResults = await Promise.all(promises);
      refreshedItems.push(...chunkResults.filter(Boolean));
    }

    order.orderItems = refreshedItems;

    return order;
  }

  buildUnitaryValue = ({
    baseValue,
    discount = 0,
    precision = 2,
    promotion,
    priceFactor = 1
  }: {
    baseValue: number;
    discount?: number;
    precision?: number;
    promotion?: PromotionInterface;
    priceFactor?: number;
  }): number => {
    if (promotion?.fixedPrice) {
      return toDecimals(promotion.fixedPrice * priceFactor, precision);
    }

    return toDecimals(toDecimals(baseValue * ((100 - discount) / 100), precision) * priceFactor, precision);
  };

  buildDiscountPercentage = ({
    baseValue,
    precision = 2,
    unitaryValue
  }: {
    baseValue: number;
    precision?: number;
    unitaryValue: number;
  }): number => {
    return toDecimals((1 - unitaryValue / baseValue) * 100, precision);
  };

  buildSoldPrice = ({
    packingQuantity = 1,
    precision = 2,
    unitaryValue
  }: {
    packingQuantity?: number;
    precision?: number;
    unitaryValue: number;
  }): number => {
    return toDecimals(unitaryValue * packingQuantity, precision);
  };

  buildTotalPrice = ({
    soldQuantity = 1,
    precision = 2,
    soldPrice
  }: {
    soldQuantity?: number;
    precision?: number;
    soldPrice: number;
  }): number => {
    return toDecimals(soldPrice * soldQuantity, precision);
  };
}

export default new OrderService();
